/**
 * Merging of AdminSegment content across languages.
 *
 * An AdminSegment exists once in local storage no matter how many languages have been
 * bundled/downloaded for it. A download built for one language carries English (languageTypeId
 * '1') first, then that language's AdminHtmlQuestionDto.qText / AdminHtmlAnswerDto.aText. When a
 * segment is already stored, the incoming download's structure (pages, scripts, ...) wins, and
 * the text of languages the incoming download does not carry is kept from what was stored.
 *
 * Works on the table form content records hold: { Data: { Columns, Rows } } where Rows[0]'s
 * content_body column is the base64-encoded AdminSegmentDto JSON.
 */

const ENGLISH = '1';

type AnyRecord = Record<string, any>;

// ── base64 / JSON body ────────────────────────────────────────────────────────────────────

const decodeBase64Utf8 = (base64: string): string => {
  const NodeBuffer = (globalThis as any).Buffer;
  if (NodeBuffer) {
    return NodeBuffer.from(base64, 'base64').toString('utf-8');
  }
  const binary = atob(base64);
  if (typeof (globalThis as any).TextDecoder === 'function') {
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new (globalThis as any).TextDecoder('utf-8').decode(bytes);
  }
  return binary;
};

/**
 * Encodes as ASCII-only JSON (non-ASCII escaped as \uXXXX) so the body decodes to the same
 * object whether a consumer reads it as UTF-8 (Buffer) or as latin1 (plain atob()).
 */
const encodeJsonBody = (value: unknown): string => {
  const ascii = JSON.stringify(value).replace(
    /[\u0080-￿]/g,
    ch => '\\u' + ch.charCodeAt(0).toString(16).padStart(4, '0')
  );
  const NodeBuffer = (globalThis as any).Buffer;
  return NodeBuffer ? NodeBuffer.from(ascii, 'utf-8').toString('base64') : btoa(ascii);
};

const findBodyColumn = (tableData: any): number => {
  const columns: string[] | undefined = tableData?.Data?.Columns;
  if (!Array.isArray(columns) || !Array.isArray(tableData?.Data?.Rows) || tableData.Data.Rows.length === 0) {
    return -1;
  }
  return columns.findIndex(c => String(c).toLowerCase() === 'content_body');
};

const readSegmentDto = (tableData: any): AnyRecord | null => {
  const bodyIdx = findBodyColumn(tableData);
  if (bodyIdx < 0) {
    return null;
  }
  const encoded = tableData.Data.Rows[0][bodyIdx];
  if (typeof encoded !== 'string' || !encoded) {
    return null;
  }
  try {
    const dto = JSON.parse(decodeBase64Utf8(encoded));
    return dto && typeof dto === 'object' ? dto : null;
  } catch {
    return null;
  }
};

// ── DTO merge ─────────────────────────────────────────────────────────────────────────────

// DTO property casing varies (Pages/pages, HtmlQuestion/htmlQuestion, ...): resolve the name the
// object actually uses so merged output keeps the incoming content's casing.
const keyOf = (obj: AnyRecord, camel: string): string => {
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  if (camel in obj) return camel;
  if (pascal in obj) return pascal;
  return camel;
};
const get = (obj: AnyRecord | null | undefined, camel: string): any => {
  if (!obj) return undefined;
  return obj[keyOf(obj, camel)];
};

const languageOf = (item: AnyRecord): string => String(get(item, 'languageTypeId') ?? '');

/** English first, otherwise original order. */
const englishFirst = <T extends AnyRecord>(items: T[]): T[] => [
  ...items.filter(i => languageOf(i) === ENGLISH),
  ...items.filter(i => languageOf(i) !== ENGLISH),
];

/**
 * Incoming items win; stored items are kept only for languages the incoming list does not carry.
 */
const mergeByLanguage = <T extends AnyRecord>(existing: T[] | undefined, incoming: T[] | undefined): T[] => {
  const inc = Array.isArray(incoming) ? incoming : [];
  const old = Array.isArray(existing) ? existing : [];
  const incomingLanguages = new Set(inc.map(languageOf));
  return englishFirst([...inc, ...old.filter(i => !incomingLanguages.has(languageOf(i)))]);
};

const mergeHtmlQuestions = (existing: AnyRecord[] | undefined, incoming: AnyRecord[] | undefined): AnyRecord[] => {
  const inc = Array.isArray(incoming) ? incoming : [];
  const old = Array.isArray(existing) ? existing : [];

  const merged = inc.map(incomingQuestion => {
    const stored = old.find(o => languageOf(o) === languageOf(incomingQuestion));
    if (!stored) {
      return incomingQuestion;
    }
    // Same-language question: the incoming text wins, but a question's answer list can carry
    // several languages' answers — keep stored answers for languages the incoming list lacks.
    const result = { ...incomingQuestion };
    for (const answerKey of ['htmlAnswer', 'htmlUvarAns']) {
      const incomingKey = keyOf(incomingQuestion, answerKey);
      const storedKey = keyOf(stored, answerKey);
      if (Array.isArray(incomingQuestion[incomingKey]) || Array.isArray(stored[storedKey])) {
        result[incomingKey] = mergeByLanguage(stored[storedKey], incomingQuestion[incomingKey]);
      }
    }
    return result;
  });

  const incomingLanguages = new Set(inc.map(languageOf));
  return englishFirst([...merged, ...old.filter(o => !incomingLanguages.has(languageOf(o)))]);
};

/** Calls visit(key, question) for every question in Pages[] and Nodes[].Pages[]. */
const walkQuestions = (dto: AnyRecord, visit: (key: string, question: AnyRecord) => void): void => {
  const walkPages = (pages: any, prefix: string) => {
    if (!Array.isArray(pages)) return;
    pages.forEach((page: AnyRecord, pageIndex: number) => {
      const pageKey = `${prefix}p:${get(page, 'htmPageId') ?? get(page, 'pageNum') ?? pageIndex}`;
      const questions = get(page, 'questions');
      if (!Array.isArray(questions)) return;
      questions.forEach((entry: AnyRecord, entryIndex: number) => {
        const question = get(entry, 'question');
        if (!question) return;
        const key = `${pageKey}|${get(question, 'mvarName') ?? ''}|${get(entry, 'qListNum') ?? entryIndex}`;
        visit(key, question);
      });
    });
  };

  walkPages(get(dto, 'pages'), '');
  const nodes = get(dto, 'nodes');
  if (Array.isArray(nodes)) {
    nodes.forEach((node: AnyRecord, nodeIndex: number) => {
      walkPages(get(node, 'pages'), `n:${get(node, 'nodeId') ?? nodeIndex}/`);
    });
  }
};

/** incoming's structure, plus the languages' text only the stored segment has. */
export const mergeAdminSegmentDtos = (existing: AnyRecord, incoming: AnyRecord): AnyRecord => {
  const merged: AnyRecord = JSON.parse(JSON.stringify(incoming));

  const storedQuestions = new Map<string, AnyRecord>();
  walkQuestions(existing, (key, question) => storedQuestions.set(key, question));
  walkQuestions(merged, (key, question) => {
    const stored = storedQuestions.get(key);
    if (!stored) return;
    const htmlKey = keyOf(question, 'htmlQuestion');
    const storedHtml = get(stored, 'htmlQuestion');
    if (Array.isArray(question[htmlKey]) || Array.isArray(storedHtml)) {
      question[htmlKey] = mergeHtmlQuestions(storedHtml, question[htmlKey]);
    }
  });

  // Preloads are per-language too (FieldCode + LanguageTypeId).
  const incomingPreloadKeys = new Set<string>(
    (get(merged, 'preloads') ?? []).map((p: AnyRecord) => `${get(p, 'fieldCode')}|${languageOf(p)}`)
  );
  const storedPreloads: AnyRecord[] = (get(existing, 'preloads') ?? [])
    .filter((p: AnyRecord) => !incomingPreloadKeys.has(`${get(p, 'fieldCode')}|${languageOf(p)}`));
  if (storedPreloads.length > 0) {
    merged[keyOf(merged, 'preloads')] = [...(get(merged, 'preloads') ?? []), ...storedPreloads];
  }

  // The stored segment is now multi-language; English is its base language.
  if (languageOf(existing) === ENGLISH || languageOf(merged) === ENGLISH) {
    merged[keyOf(merged, 'languageTypeId')] = ENGLISH;
  }

  return merged;
};

// ── Table (content record) merge ──────────────────────────────────────────────────────────

/**
 * Merges two AdminSegment content tables into one. Returns the incoming table unchanged when
 * either body can't be read (nothing to merge safely).
 */
export const mergeAdminSegmentTableData = <T extends AnyRecord>(existing: T, incoming: T): T => {
  const existingDto = readSegmentDto(existing);
  const incomingDto = readSegmentDto(incoming);
  if (!existingDto || !incomingDto) {
    return incoming;
  }

  const bodyIdx = findBodyColumn(incoming);
  const merged: any = { ...incoming, Data: { ...incoming.Data, Rows: incoming.Data.Rows.map((r: any[]) => [...r]) } };
  merged.Data.Rows[0][bodyIdx] = encodeJsonBody(mergeAdminSegmentDtos(existingDto, incomingDto));
  return merged as T;
};
